import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { TrashIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { AdminGuard } from '@/components/layout/AdminGuard';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ApiRequestError, api } from '@/lib/api-client';
import { handleApiError } from '@/lib/handle-error';

/**
 * Admin analytics dashboard for Atlas (plan D-analytics). Reads:
 *   - GET /atlas/usage/admin?days=N&groupBy=tool|user|intent
 *
 * The endpoint scans `atlas/auditLog` for the window and aggregates
 * client-side, so the response is small (≤ 100 rows typically) and
 * the page stays cheap. Refetches on tab change via React Query keying.
 *
 * Gated by `<AdminGuard>` — same pattern as webhook-logs / users /
 * tokens. Server enforces `requireRole('admin')` regardless; the
 * guard is for fast UX so non-admins see a clear 403 page rather
 * than waiting on the request to fail.
 */

export const Route = createFileRoute('/_authed/admin/atlas')({
  component: AtlasAdminPage,
});

type GroupBy = 'tool' | 'user' | 'intent';

interface AdminAggregateRow {
  key: string;
  callCount: number;
  errorCount: number;
  totalTokensIn: number;
  totalTokensOut: number;
  totalCostUsd: number;
  avgDurationMs: number;
}

interface UsageSnapshot {
  totalTokensIn: number;
  totalTokensOut: number;
  totalCachedIn: number;
  totalCostUsd: number;
  callCount: number;
  applyCount: number;
}

interface AdminUsageResponse {
  endDate: string;
  days: number;
  groupBy: GroupBy;
  rows: AdminAggregateRow[];
  orgTotals: UsageSnapshot;
  orgApplyRate: number;
}

function AtlasAdminPage() {
  return (
    <AdminGuard>
      <AtlasAdminContent />
    </AdminGuard>
  );
}

function AtlasAdminContent() {
  const [groupBy, setGroupBy] = useState<GroupBy>('tool');
  const days = 7;

  const usage = useQuery<AdminUsageResponse>({
    queryKey: ['atlas', 'usage', 'admin', days, groupBy],
    queryFn: () => api<AdminUsageResponse>(`/atlas/usage/admin?days=${days}&groupBy=${groupBy}`),
    staleTime: 30_000,
  });

  return (
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-6 px-4 py-8 sm:px-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Atlas analytics</h1>
        <p className="text-sm text-muted-foreground">
          Aggregated audit log + usage rollup for the last {days} days. Live data, refreshed on tab
          change. Org-wide; per-user breakdown via the "User" tab.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-4">
        <KpiCard
          label="Total calls"
          value={usage.data?.orgTotals.callCount ?? null}
          loading={usage.isPending}
        />
        <KpiCard
          label="Total cost"
          value={usage.data ? `$${usage.data.orgTotals.totalCostUsd.toFixed(2)}` : null}
          loading={usage.isPending}
        />
        <KpiCard
          label="Apply rate"
          value={usage.data ? `${Math.round(usage.data.orgApplyRate * 100)}%` : null}
          loading={usage.isPending}
          hint={
            usage.data
              ? `${usage.data.orgTotals.applyCount} applies / ${usage.data.orgTotals.callCount} calls`
              : undefined
          }
        />
        <KpiCard
          label="Cache hit"
          value={
            usage.data && usage.data.orgTotals.totalTokensIn > 0
              ? `${Math.round(
                  (usage.data.orgTotals.totalCachedIn / usage.data.orgTotals.totalTokensIn) * 100,
                )}%`
              : usage.data
                ? '0%'
                : null
          }
          loading={usage.isPending}
        />
      </div>

      <Tabs value={groupBy} onValueChange={(v) => setGroupBy(v as GroupBy)}>
        <TabsList>
          <TabsTrigger value="tool">By tool</TabsTrigger>
          <TabsTrigger value="user">By user</TabsTrigger>
          <TabsTrigger value="intent">By intent</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="rounded-lg border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{labelFor(groupBy)}</TableHead>
              <TableHead className="text-right">Calls</TableHead>
              <TableHead className="text-right">Errors</TableHead>
              <TableHead className="text-right">Tokens in</TableHead>
              <TableHead className="text-right">Tokens out</TableHead>
              <TableHead className="text-right">Cost</TableHead>
              <TableHead className="text-right">Avg latency</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {usage.isPending ? (
              Array.from({ length: 6 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell colSpan={7}>
                    <Skeleton className="h-6 w-full" />
                  </TableCell>
                </TableRow>
              ))
            ) : usage.isError ? (
              <TableRow>
                <TableCell colSpan={7} className="py-12 text-center text-destructive">
                  Failed to load analytics. Make sure you are signed in as admin and the audit log
                  index is up to date.
                </TableCell>
              </TableRow>
            ) : usage.data && usage.data.rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="py-12 text-center text-muted-foreground">
                  No Atlas activity in the last {days} days.
                </TableCell>
              </TableRow>
            ) : (
              usage.data?.rows.map((row) => (
                <TableRow key={row.key}>
                  <TableCell className="font-mono text-xs">{row.key}</TableCell>
                  <TableCell className="text-right">{row.callCount}</TableCell>
                  <TableCell className="text-right">
                    {row.errorCount > 0 ? (
                      <Badge variant="destructive">{row.errorCount}</Badge>
                    ) : (
                      <span className="text-muted-foreground">0</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right text-xs">{row.totalTokensIn}</TableCell>
                  <TableCell className="text-right text-xs">{row.totalTokensOut}</TableCell>
                  <TableCell className="text-right text-xs">
                    ${row.totalCostUsd.toFixed(4)}
                  </TableCell>
                  <TableCell className="text-right text-xs">{row.avgDurationMs}ms</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <ToolCostCatalogPanel />
      <GdprDeletePanel />
    </div>
  );
}

interface CostEstimate {
  toolName: string;
  intent: 'creator' | 'reviewer' | 'semantic-layer';
  promptTokens: number;
  outputTokens: number;
  estCostUsd: number;
  model: 'opus';
}

const KNOWN_TOOLS = [
  'findSimilarMetrics',
  'proposeMetricDraft',
  'proposeMetricDraftPlus',
  'improveDescription',
  'suggestDimensions',
  'generateSqlSkeleton',
  'generateMetricFlowYaml',
  'validateSemanticDefinition',
  'explainBreakingChange',
  'briefReviewDraft',
  'askDataQuestion',
  'suggestDbtTests',
  'glossaryLookup',
] as const;

/**
 * Tool cost catalog (E-cost). Calls `GET /atlas/cost-estimate?tool=X`
 * for each known tool and renders the projected upper-bound cost.
 * Pure read — no Firestore writes — so the panel is safe to render
 * on every admin visit. Cached 5 min via React Query.
 */
function ToolCostCatalogPanel() {
  const estimates = useQuery<Array<CostEstimate | null>>({
    queryKey: ['atlas', 'cost-estimate', 'all'],
    queryFn: () =>
      Promise.all(
        KNOWN_TOOLS.map((t) => api<CostEstimate | null>(`/atlas/cost-estimate?tool=${t}`)),
      ),
    staleTime: 5 * 60_000,
  });

  return (
    <section className="space-y-2">
      <div>
        <h2 className="text-base font-semibold">Tool cost catalog</h2>
        <p className="text-xs text-muted-foreground">
          Conservative upper bound per call, computed from the static prompt/output token budgets
          and the current Anthropic per-Mtok rates. Actual cost (post-call) is recorded on each
          audit row.
        </p>
      </div>
      <div className="rounded-lg border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Tool</TableHead>
              <TableHead>Model</TableHead>
              <TableHead className="text-right">Prompt tokens</TableHead>
              <TableHead className="text-right">Output tokens</TableHead>
              <TableHead className="text-right">Est. cost</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {estimates.isPending
              ? Array.from({ length: 6 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={5}>
                      <Skeleton className="h-6 w-full" />
                    </TableCell>
                  </TableRow>
                ))
              : estimates.data?.map((est, i) =>
                  est ? (
                    <TableRow key={est.toolName}>
                      <TableCell className="font-mono text-xs">{est.toolName}</TableCell>
                      <TableCell className="text-xs">
                        <Badge variant="outline" className="text-[10px]">
                          {est.model}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right text-xs">{est.promptTokens}</TableCell>
                      <TableCell className="text-right text-xs">{est.outputTokens}</TableCell>
                      <TableCell className="text-right font-mono text-xs">
                        ${est.estCostUsd.toFixed(4)}
                      </TableCell>
                    </TableRow>
                  ) : (
                    <TableRow key={KNOWN_TOOLS[i]}>
                      <TableCell colSpan={5} className="text-xs text-muted-foreground">
                        {KNOWN_TOOLS[i]}: no estimate available
                      </TableCell>
                    </TableRow>
                  ),
                )}
          </TableBody>
        </Table>
      </div>
    </section>
  );
}

/**
 * GDPR right-to-delete UI (E-gdpr). Admin types the user's email
 * twice — the server endpoint requires `confirmEmail === pathEmail`.
 * Two-step confirm in the UI ALSO defends against typos: enter →
 * preview → confirm. Reuses the destructive `<Button variant=...>` so
 * the visual contract matches Firestore-destructive admin actions.
 */
function GdprDeletePanel() {
  const [email, setEmail] = useState('');
  const [confirm, setConfirm] = useState('');
  const matches = useMemo(() => email.length > 0 && email === confirm, [email, confirm]);

  const mutation = useMutation({
    mutationFn: async () => {
      return api<{
        email: string;
        sessionsDeleted: number;
        feedbackDeleted: number;
        usageDeleted: number;
      }>(`/atlas/user/${encodeURIComponent(email)}/data`, {
        method: 'DELETE',
        body: { confirmEmail: email },
      });
    },
    onSuccess: (data) => {
      toast.success(
        `Wiped ${data.sessionsDeleted} sessions, ${data.feedbackDeleted} feedback rows, ${data.usageDeleted} usage rollups for ${data.email}.`,
      );
      setEmail('');
      setConfirm('');
    },
    onError: (err) => {
      if (err instanceof ApiRequestError && err.status === 403) {
        toast.error('Forbidden — only admins can delete Atlas user data.');
        return;
      }
      handleApiError(err, 'Atlas user-data delete failed');
    },
  });

  return (
    <section className="space-y-2">
      <div>
        <h2 className="text-base font-semibold">GDPR — delete a user's Atlas data</h2>
        <p className="text-xs text-muted-foreground">
          Wipes the user's sessions, feedback rows, and daily usage rollups. The org rollup + audit
          log are PRESERVED (compliance breadcrumb stays). This action cannot be undone.
        </p>
      </div>
      <Card className="space-y-3 p-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="flex flex-col gap-1">
            <Label htmlFor="gdpr-email">User email</Label>
            <Input
              id="gdpr-email"
              type="email"
              placeholder="someone@kiwibot.com"
              value={email}
              onChange={(e) => setEmail(e.target.value.trim())}
              disabled={mutation.isPending}
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="gdpr-confirm">Type it again to confirm</Label>
            <Input
              id="gdpr-confirm"
              type="email"
              placeholder="someone@kiwibot.com"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value.trim())}
              disabled={mutation.isPending}
            />
          </div>
        </div>
        <div className="flex items-center justify-end">
          <Button
            type="button"
            variant="destructive"
            disabled={!matches || mutation.isPending}
            onClick={() => mutation.mutate()}
            className="gap-1"
          >
            <TrashIcon className="size-3.5" />
            {mutation.isPending ? 'Deleting…' : 'Delete Atlas data'}
          </Button>
        </div>
      </Card>
    </section>
  );
}

function KpiCard({
  label,
  value,
  loading,
  hint,
}: {
  label: string;
  value: string | number | null;
  loading: boolean;
  hint?: string;
}) {
  return (
    <Card className="flex flex-col gap-1 p-4">
      <span className="text-xs uppercase tracking-wider text-muted-foreground">{label}</span>
      {loading ? (
        <Skeleton className="h-7 w-20" />
      ) : (
        <span className="text-2xl font-semibold">{value ?? '—'}</span>
      )}
      {hint && <span className="text-[11px] text-muted-foreground">{hint}</span>}
    </Card>
  );
}

function labelFor(g: GroupBy): string {
  if (g === 'tool') return 'Tool';
  if (g === 'user') return 'User';
  return 'Intent';
}
